// #ux-redesign-start: ImageCropModal migrado a Modal del DS.
// Antes: Reactstrap Modal/ModalHeader/ModalBody/ModalFooter + Buttons color="info"/danger
// con look del template Now UI. Ahora: Modal DS (theme-aware, bottom-sheet en mobile),
// botones DS (primary/secondary), padding tokenizado.
// Lógica de crop intacta (ReactCrop + canvas).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslate } from 'react-translate';

import { Modal, Button } from 'components/ds';

const ImageCropModal = (props) => {
  const {
    toggle,
    showImageCrop,
    imageInputId,
    imagePreviewId,
    defaultImage,
    setAvatar,
  } = props;

  const imgRef = useRef(null);
  const [upImg, setUpImg] = useState(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 30,
    x: 25,
    y: 25,
    aspect: 1 / 1,
    maxWidth: 1500,
    maxHeight: 1500,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const t = useTranslate('image-crop-modal');

  const imageInput = document.getElementById(imageInputId);
  const imagePreview = document.getElementById(imagePreviewId);

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  const handleImageCrop = (e) => {
    toggle();
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setUpImg(reader.result));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  useEffect(() => {
    const showPreview = (canvas) => {
      if (!canvas || !imagePreview) return;
      canvas.toBlob(
        (blob) => {
          const previewUrl = window.URL.createObjectURL(blob);
          imagePreview.src = previewUrl;
          setAvatar(blob);
        },
        'image/png',
        1
      );
    };
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const c = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    canvas.width = c.width * pixelRatio;
    canvas.height = c.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      c.x * scaleX,
      c.y * scaleY,
      c.width * scaleX,
      c.height * scaleY,
      0,
      0,
      c.width,
      c.height
    );
    showPreview(canvas);
    // eslint-disable-next-line
  }, [completedCrop]);

  useEffect(() => {
    if (imageInput) {
      imageInput.addEventListener('change', handleImageCrop);
    }
    return () => {
      if (imageInput) {
        imageInput.removeEventListener('change', handleImageCrop);
      }
    };
  });

  const handleCancel = () => {
    if (imageInput) imageInput.value = null;
    if (imagePreview) imagePreview.src = defaultImage;
    toggle();
  };

  return (
    <Modal
      isOpen={showImageCrop}
      onClose={toggle}
      title={t('ajust-your-image')}
      size="lg"
    >
      <div className="ds-imagecrop">
        <div className="ds-imagecrop__crop">
          <ReactCrop
            src={upImg}
            onImageLoaded={onLoad}
            crop={crop}
            onChange={(e) => setCrop(e)}
            onComplete={(e) => setCompletedCrop(e)}
          />
        </div>
        <canvas
          ref={previewCanvasRef}
          style={{
            display: 'none',
            width: Math.round(completedCrop?.width ?? 0),
            height: Math.round(completedCrop?.height ?? 0),
          }}
        />
        <div className="ds-imagecrop__actions">
          <Button variant="secondary" onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={toggle}>
            {t('ready')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ImageCropModal.defaultProps = {
  showImageCrop: false,
};

ImageCropModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  setAvatar: PropTypes.func.isRequired,
  showImageCrop: PropTypes.bool.isRequired,
  imageInputId: PropTypes.string.isRequired,
  imagePreviewId: PropTypes.string.isRequired,
  defaultImage: PropTypes.string.isRequired,
};

export default ImageCropModal;
// #ux-redesign-end
